import { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Copy, Loader2, Share2, X } from 'lucide-react';
import { createCaptureShare } from '../../services/api';
import { getEventCorrelation } from '../../utils/eventCorrelation';
import type { CaptureSharingSuggestion } from './types';

type ShareActionType = 'copy_link' | 'native_share';

function createIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID().replace(/-/g, '_');
  }
  return `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`;
}

function buildClipboardShareText(message: string, shareUrl: string): string {
  const trimmed = message.trim();
  return trimmed ? `${trimmed}\n\n${shareUrl}` : shareUrl;
}

function getCaptureEventCorrelation() {
  return getEventCorrelation(window.location.search, true);
}

interface CaptureShareModalProps {
  open: boolean;
  onClose: () => void;
  captureId: number | string | null;
  captureTitle?: string | null;
  artistName?: string | null;
  suggestions: CaptureSharingSuggestion[];
  initialSuggestionId?: string | null;
  onShareCreated?: () => void;
  nativeShareButtonStyle?: {
    background: string;
    color: string;
    boxShadow?: string;
  };
}

export default function CaptureShareModal({
  open,
  onClose,
  captureId,
  captureTitle,
  artistName,
  suggestions,
  initialSuggestionId,
  onShareCreated,
  nativeShareButtonStyle,
}: CaptureShareModalProps) {
  const [selectedReactionId, setSelectedReactionId] = useState<string | null>(null);
  const [shareMessage, setShareMessage] = useState('');
  const [shareBusy, setShareBusy] = useState(false);
  const [shareError, setShareError] = useState('');
  const [shareSuccess, setShareSuccess] = useState('');
  const [copyFeedback, setCopyFeedback] = useState(false);
  const [manualCopyText, setManualCopyText] = useState('');

  const shareAttemptLockRef = useRef(false);
  const shareIdempotencyKeyRef = useRef<string | null>(null);
  const forceClipboardModeRef = useRef(false);
  const manualCopyTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const copyFeedbackTimerRef = useRef<number | null>(null);
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);

  const selectedSuggestion = useMemo(
    () => suggestions.find((s) => s.id === selectedReactionId) || null,
    [suggestions, selectedReactionId]
  );

  const clearCopyFeedbackTimer = () => {
    if (copyFeedbackTimerRef.current !== null) {
      window.clearTimeout(copyFeedbackTimerRef.current);
      copyFeedbackTimerRef.current = null;
    }
  };

  useEffect(() => {
    if (!open) return;

    shareAttemptLockRef.current = false;
    shareIdempotencyKeyRef.current = null;
    forceClipboardModeRef.current = false;
    setShareBusy(false);
    setShareError('');
    setShareSuccess('');
    setCopyFeedback(false);
    setManualCopyText('');

    if (suggestions.length > 0) {
      const initial = suggestions.find((s) => s.id === initialSuggestionId) || suggestions[0];
      setSelectedReactionId(initial.id);
      setShareMessage(initial.message);
    } else {
      setSelectedReactionId(null);
      setShareMessage('');
    }

    requestAnimationFrame(() => {
      closeBtnRef.current?.focus();
    });
  }, [open, captureId, initialSuggestionId, suggestions]);

  useEffect(() => {
    return () => {
      clearCopyFeedbackTimer();
    };
  }, []);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose]);

  const chooseReaction = (suggestionId: string) => {
    const suggestion = suggestions.find((s) => s.id === suggestionId);
    setSelectedReactionId(suggestionId);
    setShareMessage(suggestion?.message || '');
    setShareError('');
    setShareSuccess('');
    setCopyFeedback(false);
    setManualCopyText('');
  };

  const createOrGetShareLink = async (actionType: ShareActionType): Promise<string | null> => {
    if (!captureId) return null;

    const trimmedMessage = shareMessage.trim();
    const correlation = getCaptureEventCorrelation();

    if (!shareIdempotencyKeyRef.current) {
      shareIdempotencyKeyRef.current = createIdempotencyKey();
    }

    const res = await createCaptureShare(
      captureId,
      {
        actionType,
        destination: actionType === 'copy_link' ? 'clipboard' : 'native',
        reactionId: selectedSuggestion?.id,
        message: trimmedMessage || undefined,
        sessionId: correlation.sessionId,
        visitorId: correlation.visitorId,
      },
      shareIdempotencyKeyRef.current
    );

    if (!res.ok) {
      const errJson = await res.json().catch(() => ({} as { error?: string }));
      setShareError(errJson.error || `Could not create a share link (HTTP ${res.status}). Please try again.`);
      return null;
    }

    const json = await res.json() as { data: { shareUrl: string } };
    onShareCreated?.();
    return json.data.shareUrl;
  };

  const performCopyShare = async () => {
    if (!captureId || shareBusy || shareAttemptLockRef.current) return;

    shareAttemptLockRef.current = true;
    setShareBusy(true);
    setShareError('');
    setShareSuccess('');
    setCopyFeedback(false);
    setManualCopyText('');
    const trimmedMessage = shareMessage.trim();

    try {
      const shareUrl = await createOrGetShareLink('copy_link');
      if (!shareUrl) return;

      const clipboardText = buildClipboardShareText(trimmedMessage, shareUrl);

      try {
        if (!navigator.clipboard?.writeText) {
          throw new Error('Clipboard API unavailable');
        }
        await navigator.clipboard.writeText(clipboardText);
        setShareSuccess(trimmedMessage ? 'Message and link copied' : 'Link copied');
        setCopyFeedback(true);
        clearCopyFeedbackTimer();
        copyFeedbackTimerRef.current = window.setTimeout(() => {
          setCopyFeedback(false);
          copyFeedbackTimerRef.current = null;
        }, 1400);
      } catch {
        setManualCopyText(clipboardText);
        setShareError('Automatic copy is unavailable. Copy the text below manually.');
        requestAnimationFrame(() => {
          if (manualCopyTextareaRef.current) {
            manualCopyTextareaRef.current.focus();
            manualCopyTextareaRef.current.select();
          }
        });
      }
    } catch (error: any) {
      const message = typeof error?.message === 'string' ? error.message : '';
      if (message.toLowerCase().includes('failed to fetch')) {
        setShareError('Cannot reach the sharing service. Check that local frontend and backend are running and configured to the same environment.');
      } else {
        setShareError('Sharing is unavailable right now. Please try again.');
      }
    } finally {
      shareAttemptLockRef.current = false;
      setShareBusy(false);
    }
  };

  const performNativeShare = async () => {
    if (!captureId || shareBusy || shareAttemptLockRef.current || typeof navigator.share !== 'function') return;

    shareAttemptLockRef.current = true;
    setShareBusy(true);
    setShareError('');
    setShareSuccess('');
    setCopyFeedback(false);
    setManualCopyText('');

    const trimmedMessage = shareMessage.trim();

    try {
      const shareUrl = await createOrGetShareLink('native_share');
      if (!shareUrl) return;

      const resolvedArtistName = artistName?.trim() || 'fans.audio';
      const resolvedTitle = captureTitle?.trim() || 'Track';
      const shareTitle = `${resolvedTitle} — ${resolvedArtistName}`;

      try {
        await navigator.share({ title: shareTitle, text: trimmedMessage || undefined, url: shareUrl });
        setShareSuccess('Share completed.');
      } catch (error: any) {
        if (error?.name === 'AbortError') {
          setShareError('');
          setShareSuccess('');
        } else {
          forceClipboardModeRef.current = true;
          setShareError('Native share is unavailable here. Use copy instead.');
        }
      }
    } catch (error: any) {
      const message = typeof error?.message === 'string' ? error.message : '';
      if (message.toLowerCase().includes('failed to fetch')) {
        setShareError('Cannot reach the sharing service. Check that local frontend and backend are running and configured to the same environment.');
      } else {
        setShareError('Sharing is unavailable right now. Please try again.');
      }
    } finally {
      shareAttemptLockRef.current = false;
      setShareBusy(false);
    }
  };

  const canNativeShare = !forceClipboardModeRef.current && typeof navigator.share === 'function';

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
        >
          <button className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} aria-label="Close share modal" />

          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="relative z-10 w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl border border-white/15 bg-[#121212] p-5 sm:p-6"
            role="dialog"
            aria-modal="true"
            aria-labelledby="capture-share-modal-title"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 id="capture-share-modal-title" className="text-lg font-bold text-white">Share this track</h2>
              <button ref={closeBtnRef} onClick={onClose} className="p-1.5 text-white/70 hover:text-white" aria-label="Close">
                <X className="h-4 w-4" />
              </button>
            </div>

            <p className="text-sm text-white/80 mb-3">What did you think?</p>

            {suggestions.length > 0 ? (
              <div className="flex flex-wrap gap-2 mb-4">
                {suggestions.map((suggestion) => (
                  <button
                    key={suggestion.id}
                    type="button"
                    onClick={() => chooseReaction(suggestion.id)}
                    className={`rounded-full border px-3 py-1.5 text-xs transition ${selectedReactionId === suggestion.id ? 'border-electric-cyan/70 bg-electric-cyan/15 text-electric-cyan' : 'border-white/20 bg-black/25 text-white/80 hover:border-white/45'}`}
                  >
                    {suggestion.label}
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-xs text-white/60 mb-4">No preset reactions for this capture. You can write your own message.</p>
            )}

            <label className="block text-xs uppercase tracking-wide text-white/60 mb-2">Your message (optional)</label>
            <textarea
              value={shareMessage}
              onChange={(e) => {
                setShareMessage(e.target.value.slice(0, 280));
                setShareError('');
                setShareSuccess('');
                setManualCopyText('');
              }}
              rows={3}
              className="w-full rounded-xl border border-white/20 bg-black/30 px-3 py-2 text-sm text-white placeholder-white/35 focus:border-electric-cyan/60 focus:outline-none"
              placeholder="Add a note before sharing"
            />
            <div className="mt-1 text-right text-[11px] text-white/45">{shareMessage.length}/280</div>

            {manualCopyText ? (
              <div className="mt-3">
                <label className="block text-[11px] uppercase tracking-wide text-white/55 mb-1">Manual copy fallback</label>
                <textarea
                  ref={manualCopyTextareaRef}
                  readOnly
                  value={manualCopyText}
                  rows={4}
                  onFocus={(e) => e.currentTarget.select()}
                  className="w-full rounded-xl border border-white/20 bg-black/40 px-3 py-2 text-xs text-white/90 focus:border-electric-cyan/60 focus:outline-none"
                />
              </div>
            ) : null}

            {shareError && <p className="mt-2 text-xs text-red-400">{shareError}</p>}
            {shareSuccess && <p className="mt-2 text-xs text-green-400">{shareSuccess}</p>}

            <div className="mt-4 space-y-2">
              <button
                type="button"
                disabled={shareBusy || !captureId}
                onClick={performCopyShare}
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-white/20 bg-black/25 px-4 py-3 text-sm font-semibold text-white/90 hover:border-white/45 disabled:opacity-60"
              >
                {shareBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Copy className="h-4 w-4" />}
                {copyFeedback ? 'Copied!' : 'Copy Link & Message'}
              </button>

              {canNativeShare ? (
                <button
                  type="button"
                  disabled={shareBusy || !captureId}
                  onClick={performNativeShare}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold disabled:opacity-60"
                  style={nativeShareButtonStyle || {
                    background: 'rgba(0, 245, 255, 0.9)',
                    color: '#0D0D0D',
                    boxShadow: '0 0 10px rgba(0, 245, 255, 0.6), 0 0 25px rgba(0, 245, 255, 0.3), 0 0 45px rgba(0, 245, 255, 0.15)',
                  }}
                >
                  {shareBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />}
                  Share
                </button>
              ) : null}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
